import type { Cache } from '@wix/thunderbolt-ssr-api'
import type {
	AppSpecData,
	BuilderCompData,
	BuilderComponentsData,
	BuilderCompTranslations,
	BuilderPublicPackagesUrls,
	ViewerClientSpecMap,
} from '@wix/thunderbolt-symbols'
import { NoopCache } from '../withCache'

export const VIEWER_ASSETS_BASE_URL = 'https://viewer-assets.parastorage.com'
const LOCAL_PILER_STATICS = 'http://localhost:3003'

export const isPilerComponent = (componentUrl: string) => componentUrl.includes('piler-router')

export type IGetBuilderComponentsData = (
	clientSpecMap: ViewerClientSpecMap,
	isBuilderComponentModel?: boolean,
	serviceTopology?: Record<string, string>,
	builderPublicManifest?: Record<string, string>,
	useLocalPiler?: boolean,
	debug?: boolean,
	shouldDisableBuilderWixComponents?: boolean,
) => BuilderComponentsData

export type IGetBuilderComponentsTranslations = (
	clientSpecMap: ViewerClientSpecMap
) => Record<string, BuilderCompTranslations>

const builderPublicPackages = [
	'builder-interactions',
	'builder-state',
	'builder-state-react',
	'builder-velo',
	'builder-velo-react',
	'builder-wix-components',
	'builder-element-removal',
	'builder-utils',
]

export const PILER_SITEASSETS_PACKAGE = '@wix/piler-siteassets'

export const getBuilderPublicManifest = async (
	baseUrl: string,
	version: string,
	cache: Cache<string, string> = NoopCache()
): Promise<Record<string, string>> => {
	const url = `${baseUrl}/builder-public-manifests/${version}/piler-siteassets-manifest.json`

	if (cache.get(url)) {
		return JSON.parse(cache.get(url)!)
	}

	return fetch(url)
		.then((res) => {
			if (!res.ok) {
				throw new Error(`failed to fetch builder public manifest from ${url}: ${res.statusText}`)
			}
			return res.text()
		})
		.then((txt) => {
			cache.set(url, txt)
			return JSON.parse(txt)
		})
}

export const getPublicPackagesUrls = (
	serviceTopology: Record<string, string>,
	builderPublicManifest: Record<string, string> = {},
	useLocalPiler = false,
	debug = false,
	shouldDisableBuilderWixComponents = false
): BuilderPublicPackagesUrls => {
	const getUrlFor = (pkg: string) => {
		const base = serviceTopology[pkg]
		const url = builderPublicManifest[pkg] ? `${base.replace(/\/[^/]+$/, '')}/${builderPublicManifest[pkg]}` : base

		if (pkg === 'services-manager-react') {
			return url
		}
		if (useLocalPiler) {
			return LOCAL_PILER_STATICS
		}
		return debug ? `${url}/debug` : url
	}

	const builderBundleUrlFor = (pkg: string, format: string) => {
		const suffix = format === 'es' ? 'mjs' : 'js'
		const baseUrl = getUrlFor(pkg)
		return `${baseUrl}/${format}/${pkg}.${suffix}`
	}

	const urls = {
		umd: {
			...(!shouldDisableBuilderWixComponents && {
				'builder-wix-components-sdks': `${getUrlFor('builder-wix-components')}/sdks/umd/sdks.js`,
			}),
		},
		esm: {
			'services-manager-react': process.env.browser
				? `${getUrlFor('services-manager-react')}/browser/index.js`
				: `${getUrlFor('services-manager-react')}/index.js`,
			...(!shouldDisableBuilderWixComponents && {
				'builder-wix-components-sdks': `${getUrlFor('builder-wix-components')}/sdks/es/sdks.mjs`,
			}),
		},
	} as BuilderPublicPackagesUrls

	return builderPublicPackages.reduce((acc, pkg) => {
		if (shouldDisableBuilderWixComponents && pkg === 'builder-wix-components') {
			return acc
		}
		acc.umd[pkg] = builderBundleUrlFor(pkg, 'umd')
		acc.esm[pkg] = builderBundleUrlFor(pkg, 'es')
		return acc
	}, urls)
}

export const getBuilderComponentsData: IGetBuilderComponentsData = (
	clientSpecMap,
	isBuilderComponentModel,
	serviceTopology,
	builderPublicManifest,
	useLocalPiler,
	debug,
	shouldDisableBuilderWixComponents
): BuilderComponentsData => {
	// If we are not in builder component model (e.g. in studio/classic), we don't want to load builder components data
	if (!isBuilderComponentModel) {
		return {
			components: {},
			publicPackagesUrls: {},
		} as BuilderComponentsData
	}
	const componentsData = Object.values(clientSpecMap).reduce((acc, spec: AppSpecData) => {
		const { appDefinitionId, components } = spec
		if (!components) {
			return acc
		}

		components
			.filter((comp) => comp.type === 'EDITOR_REACT_COMPONENT')
			.forEach((comp) => {
				const { componentId, data } = comp
				const { type, resources } = data
				const sdkUrl = resources.sdk?.url
				const serverUrl = resources.server?.url || ''
				const editorComponentUrl = resources.editor?.componentUrl
				const editorCssUrl = resources.editor?.cssUrl
				const componentUrl = resources.client?.componentUrl || ''
				const moduleSpecifier = resources.client?.moduleSpecifier

				const translations = resources.translations
				const viewerReducedModel = resources.viewerReducedModel
				const viewerRuntimeCss = resources.viewerRuntimeCss

				acc[type] = {
					appDefinitionId,
					componentId,
					componentType: type,
					componentUrl,
					editorComponentUrl,
					cssUrl: resources?.client?.cssUrl || '',
					editorCssUrl,
					viewerReducedModel,
					viewerRuntimeCss,
					...(sdkUrl && {
						sdkUrl,
					}),
					...(serverUrl && {
						serverUrl,
					}),
					...(translations && {
						translations,
					}),
					...(moduleSpecifier && {
						moduleSpecifier,
					}),
				}
			})

		return acc
	}, {} as Record<string, BuilderCompData>)

	return {
		components: componentsData,
		publicPackagesUrls: serviceTopology ? getPublicPackagesUrls(serviceTopology, builderPublicManifest, useLocalPiler, debug, shouldDisableBuilderWixComponents) : {},
	} as BuilderComponentsData
}

export const getBuilderComponentsTranslations: IGetBuilderComponentsTranslations = (clientSpecMap) => {
	return Object.values(clientSpecMap).reduce((acc, spec: AppSpecData) => {
		const { components } = spec
		if (!components) {
			return acc
		}

		components
			.filter((comp) => comp.type === 'EDITOR_REACT_COMPONENT')
			.forEach((comp) => {
				const { data } = comp
				const { type, resources } = data

				const translations = resources.translations

				if (translations) {
					acc[type] = translations
				}
			})

		return acc
	}, {} as Record<string, BuilderCompTranslations>)
}

const isBuilderData = (type: string) => type?.endsWith('.Data')

export const extractComponentData = (componentData: any): any => {
	if (!componentData || typeof componentData !== 'object') {
		return componentData
	}

	if (Array.isArray(componentData)) {
		return componentData.map((item) => extractComponentData(item))
	}

	if (isBuilderData(componentData.type) && componentData.data) {
		return extractComponentData(componentData.data)
	}

	return Object.entries(componentData)
		.reduce(
			(result, [key, value]) => ({
				...result,
				[key]: extractComponentData(value),
			}),
			{}
		)
}
